<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import { PictureOutlined } from '@ant-design/icons-vue';
import EditTitleModal from '../EditTitleModal.vue';
import EditDescriptionModal from '../EditDescriptionModal.vue';
import ImageUploadModal from '../ImageUploadModal.vue';

const b = bem('info-image-module');

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    ImageUploadModal,
    PictureOutlined,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
      isDescriptionModalVisible: false,
      isImageModalVisible: false,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    imagePosition() {
      const templateId = this.moduleData?.templateId
        || this.moduleData?.parsedContent?.templateId
        || 'info-image-left';
      if (templateId === 'info-image-right') {
        return 'right';
      }
      return 'left';
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Title';
    },
    displayDescription() {
      if (this.isMobile && this.content.descriptionMobile) {
        return this.content.descriptionMobile;
      }
      return this.content.description || 'Description';
    },
    displayImage() {
      if (this.isMobile && this.content.imageMobile) {
        return this.content.imageMobile;
      }
      return this.content.image || '';
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openImageModal() {
      if (this.isEditMode) {
        this.isImageModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    handleDescriptionSave(data) {
      this.updateContent({
        ...this.content,
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleImageSave(data) {
      this.updateContent({
        ...this.content,
        image: data.image,
        imageMobile: data.imageMobile,
      });
      this.isImageModalVisible = false;
    },
    updateContent(newContent) {
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(newContent),
      });
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, 'image-right': imagePosition === 'right', popup: isPopup })">
    <div
      v-if="!isMobile"
      :class="b('image', { editable: isEditMode, popup: isPopup })"
      :style="displayImage ? {
        backgroundImage: `url(${displayImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}"
      role="button"
      tabindex="0"
      @click="openImageModal"
      @keydown.enter="openImageModal"
    >
      <span v-if="!displayImage && !isPopup" :class="b('placeholder-text')">
        <span :class="b('placeholder-plus')">+</span><br>CLICK TO UPLOAD
      </span>
      <span v-if="!displayImage && isPopup" :class="b('placeholder-text')">
        <PictureOutlined />
      </span>
    </div>

    <div :class="b('content', { mobile: isMobile })">
      <div
        :class="b('title-row', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openTitleModal"
        @keydown.enter="openTitleModal"
      >
        <span :class="b('title')">{{ displayTitle }}</span>
        <PencilIcon v-if="isEditMode" :size="20" color="#242424" :class="b('edit-icon')" />
      </div>
      <div
        v-if="isMobile"
        :class="b('image', { editable: isEditMode, popup: isPopup, mobile: true })"
        :style="displayImage ? {
          backgroundImage: `url(${displayImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}"
      />
      <div
        :class="b('desc-row', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openDescriptionModal"
        @keydown.enter="openDescriptionModal"
      >
        <span :class="b('desc')">{{ displayDescription }}</span>
        <PencilIcon v-if="isEditMode" :size="16" color="#595959" :class="b('edit-icon')" />
      </div>
    </div>

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="content.description"
      :description-mobile="content.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
    />

    <ImageUploadModal
      :visible="isImageModalVisible"
      :image="content.image"
      :image-mobile="content.imageMobile"
      @close="isImageModalVisible = false"
      @save="handleImageSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.info-image-module {
  display: flex;
  align-items: flex-start;
  gap: 64px;
  width: 1540px;
  padding: 64px 0;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    gap: vw-full(64);
    padding: vw-full(64) 0;
  }
  &_popup {
    width: 1200px;
    gap: 50px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      gap: vw-full(50);
    }
  }
  &_mobile {
    padding: 40px 0;
  }
  &_image-right {
    flex-direction: row-reverse;
  }

  &__image {
    flex-shrink: 0;
    width: 867px;
    height: 608px;
    border-radius: 24px;
    background: #d9d9d9;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(867);
      height: vw-full(608);
      border-radius: vw-full(24);
    }

    &_editable:hover {
      background: #c5c5c5;
    }
    &_popup {
      width: 675px;
      height: 474px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(675);
        height: vw-full(474);
      }
    }
    &_mobile {
      width: 380px;
      height: 269px;
      margin: 0 auto;
    }
  }

  &__placeholder-text {
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    color: #7f7f7f;
    text-align: center;
    line-height: 1.4;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__placeholder-plus {
    font-size: 56px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(56);
    }
  }

  &__content {
    flex: 1;
    padding-top: 128px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 32px;

    @include mediaMinMax(1025, $normal) {
      padding-top: vw-full(128);
      gap: 32px;
    }
    &_popup {
      padding-top: 100px;
      gap: 24px;

      @include mediaMinMax(1025, $normal) {
        padding-top: vw-full(100);
        gap: vw-full(24)
      }
    }
    &_mobile {
      height: fit-content;
      padding: 0 16px;
      gap: 20px;
      align-items: center;
    }
  }

  &__title-row,
  &__desc-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(10);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: #293241;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }
    &_popup {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }
  }

  &__desc {
    font-family: Montserrat, sans-serif;
    font-size: 19px;
    font-weight: 400;
    color: #5e5d5d;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    margin-top: 5px;
  }
}
</style>
